import { useEffect, useRef, useState } from 'react';
import { supabase } from '../lib/supabase';
import { digitsOnly } from '../lib/validation';
import Icon from './Icon';

// ============================================================
// "Popuni sa slike" — screenshot firme (CompanyWall, APR, ugovor...)
// se šalje Supabase funkciji "read-company", koja preko Claude-a
// pročita naziv, adresu, PIB, MB i kontakt.
// ============================================================

// Smanji sliku pre slanja (brže i jeftinije), vrati base64 bez prefiksa
function imageToBase64(file) {
  return new Promise((resolve, reject) => {
    if (!file || !file.type.startsWith('image/')) { reject(new Error('Izaberite ili nalepite sliku.')); return; }
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('Slika nije mogla da se učita.'));
    reader.onload = () => {
      const img = new Image();
      img.onerror = () => reject(new Error('Slika nije mogla da se učita.'));
      img.onload = () => {
        const max = 1568;
        const scale = Math.min(1, max / Math.max(img.width, img.height));
        const w = Math.round(img.width * scale);
        const h = Math.round(img.height * scale);
        const canvas = document.createElement('canvas');
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext('2d');
        ctx.fillStyle = '#fff';
        ctx.fillRect(0, 0, w, h);
        ctx.drawImage(img, 0, 0, w, h);
        resolve(canvas.toDataURL('image/jpeg', 0.88).split(',')[1]);
      };
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  });
}

const isSerbia = (c) => !c || /^(srbija|serbia|republika srbija)$/i.test(String(c).trim());

export default function ImportFromImage({ onData }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);
  const fileRef = useRef(null);

  const read = async (file) => {
    setError('');
    setDone(false);
    try {
      const image = await imageToBase64(file);
      setBusy(true);
      const { data, error: fnError } = await supabase.functions.invoke('read-company', {
        body: { image, media_type: 'image/jpeg' },
      });
      setBusy(false);
      if (fnError) {
        let msg = fnError.message;
        try { const body = await fnError.context?.json(); if (body?.error) msg = body.error; } catch { /* ignore */ }
        setError(msg || 'Čitanje slike nije uspelo.');
        return;
      }
      if (!data || data.error) { setError(data?.error || 'Podaci nisu pronađeni na slici.'); return; }
      const foreign = !isSerbia(data.country);
      const out = {
        name: data.name || '',
        address: data.address || '',
        postal_code: data.postal_code || '',
        city: data.city || '',
        country: foreign ? (data.country || '') : 'Srbija',
        pib: foreign ? (data.pib || '') : digitsOnly(data.pib),
        mb: foreign ? (data.mb || '') : digitsOnly(data.mb),
        email: data.email || '',
        phone: data.phone || '',
        is_foreign: foreign,
      };
      // Prazna polja ne brišu ono što je već upisano
      Object.keys(out).forEach((k) => { if (out[k] === '' ) delete out[k]; });
      onData(out);
      setDone(true);
    } catch (err) {
      setBusy(false);
      setError(err.message || String(err));
    }
  };

  // Ctrl + V dok je panel otvoren
  useEffect(() => {
    if (!open) return undefined;
    const onPaste = (e) => {
      const item = Array.from(e.clipboardData?.items || []).find((i) => i.type.startsWith('image/'));
      if (item) { e.preventDefault(); read(item.getAsFile()); }
    };
    window.addEventListener('paste', onPaste);
    return () => window.removeEventListener('paste', onPaste);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  if (!open) {
    return (
      <button type="button" onClick={() => setOpen(true)} style={styles.openBtn}>
        <Icon name="fileText" size={16} />
        Popuni sa slike (screenshot sa CompanyWall-a, APR-a...)
      </button>
    );
  }

  return (
    <div style={styles.panel}>
      <div style={styles.head}>
        <span style={styles.title}>Popuni sa slike</span>
        <button type="button" onClick={() => { setOpen(false); setError(''); setDone(false); }} style={styles.closeBtn} aria-label="Zatvori">
          <Icon name="x" size={15} />
        </button>
      </div>
      <div
        role="button"
        tabIndex={0}
        onClick={() => fileRef.current?.click()}
        onKeyDown={(e) => { if (e.key === 'Enter') fileRef.current?.click(); }}
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => { e.preventDefault(); read(e.dataTransfer.files?.[0]); }}
        style={{ ...styles.drop, ...(busy ? { opacity: 0.6 } : {}) }}
      >
        <input ref={fileRef} type="file" accept="image/*" style={{ display: 'none' }}
          onChange={(e) => { read(e.target.files?.[0]); e.target.value = ''; }} />
        {busy ? (
          <strong>Čitam podatke sa slike...</strong>
        ) : (
          <>
            <strong>Nalepi screenshot (Ctrl + V)</strong>
            <span style={styles.sub}>ili prevuci sliku ovde, ili klikni da je izabereš</span>
          </>
        )}
      </div>
      {error && <div style={styles.error}>{error}</div>}
      {done && !error && <div style={styles.ok}>Podaci su popunjeni. Proveri ih pre čuvanja (PIB i MB se proveravaju automatski).</div>}
    </div>
  );
}

const styles = {
  openBtn: { display: 'flex', alignItems: 'center', gap: 8, width: '100%', minHeight: 42, marginBottom: 14, border: '1.5px dashed var(--lavender-500)', background: 'var(--lavender-100)', color: 'var(--lavender-700)', borderRadius: 10, padding: '0 14px', fontWeight: 700, fontSize: 13, textAlign: 'left' },
  panel: { border: '1.5px solid var(--lavender-500)', background: 'var(--lavender-100)', borderRadius: 8, padding: 12, marginBottom: 14 },
  head: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 },
  title: { fontSize: 13, fontWeight: 800, color: 'var(--lavender-700)' },
  closeBtn: { width: 30, height: 30, border: 'none', background: 'transparent', color: 'var(--ink-500)', borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center' },
  drop: { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 4, minHeight: 92, border: '1.5px dashed var(--lavender-500)', background: 'var(--surface)', borderRadius: 10, padding: 12, textAlign: 'center', fontSize: 14, color: 'var(--ink-900)', cursor: 'pointer' },
  sub: { fontSize: 12, color: 'var(--ink-500)' },
  error: { marginTop: 8, background: 'var(--red-100)', color: 'var(--red-600)', borderRadius: 8, padding: '8px 10px', fontSize: 12, wordBreak: 'break-word' },
  ok: { marginTop: 8, background: 'var(--forest-100)', color: 'var(--forest-600)', borderRadius: 8, padding: '8px 10px', fontSize: 12 },
};
